import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, Validators } from '@angular/forms';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap } from 'rxjs/operators';
import { DirectoryUser, UserService } from '../services/user.service';
import { AlertService } from '../services/alert.service';
import { AlertPerson } from '../alert/alert.component';
import {
  ActiveUserBlock,
  BLOCK_REASON_MAX_LENGTH,
  REQUESTOR_KEY_MAX_LENGTH,
  RequestBlockService
} from '../services/request-block.service';

// Backend identities (requestor_key, blocked_by) are Entra object IDs when the token has an oid.
const OBJECT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RATE_LIMITER = 'rate_limiter';
const PROFILE_LOOKUP_CONCURRENCY = 4;


@Component({
  selector: 'app-blocked-users',
  templateUrl: './blocked-users.component.html',
  styleUrls: ['./blocked-users.component.scss'],
  standalone: false
})
export class BlockedUsersComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  readonly keyMaxLength = REQUESTOR_KEY_MAX_LENGTH;
  readonly reasonMaxLength = BLOCK_REASON_MAX_LENGTH;

  user: any;
  blocks: ActiveUserBlock[] = [];
  loading = true;
  loadError: string | null = null;
  submitting = false;
  unblockingKey: string | null = null;

  // Keyed by lowercased identity; null means the lookup found no unique directory user.
  private readonly profiles = new Map<string, AlertPerson | null>();

  readonly blockForm = this.fb.nonNullable.group({
    requestorKey: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(REQUESTOR_KEY_MAX_LENGTH)]],
    reason: ['', [Validators.maxLength(BLOCK_REASON_MAX_LENGTH)]]
  });

  constructor(
    private _user: UserService,
    private _blocks: RequestBlockService,
    private _alert: AlertService
  ) {}

  ngOnInit(): void {
    this._user.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (!user) return;
        const wasAdmin = !!this.user?.isAdmin;
        this.user = user;
        if (user.isAdmin && !wasAdmin) {
          this.refresh();
        } else if (!user.isAdmin) {
          this.loading = false;
        }
      });
  }

  trackBlock = (_: number, block: ActiveUserBlock) => block.requestor_key;

  refresh(): void {
    this.loading = true;
    this.loadError = null;
    this._blocks.listBlocks()
      .pipe(
        finalize(() => this.loading = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: res => {
          this.blocks = [...(res?.blocks ?? [])].sort(
            (a, b) => new Date(b.blocked_at).getTime() - new Date(a.blocked_at).getTime()
          );
          this.resolveProfiles();
        },
        error: (err: HttpErrorResponse) => {
          this.loadError = this.describeError(err, 'load blocked users');
        }
      });
  }

  async submitBlock(): Promise<void> {
    if (this.submitting) return;
    if (this.blockForm.invalid) {
      this.blockForm.markAllAsTouched();
      return;
    }

    const identifier = this.blockForm.controls.requestorKey.value.trim();
    const reason = this.blockForm.controls.reason.value.trim();

    this.submitting = true;
    this.blockForm.disable();
    try {
      const requestorKey = await this.resolveRequestorKey(identifier);
      if (!requestorKey) return;

      if (this.blocks.some(b => b.requestor_key === requestorKey)) {
        await this._alert.warning('This user is already blocked.', identifier);
        return;
      }

      await firstValueFrom(this._blocks.blockUser({ requestor_key: requestorKey, ...(reason ? { reason } : {}) }));
      this.blockForm.reset();
      this.refresh();
    } catch (err) {
      await this._alert.error(this.describeError(err as HttpErrorResponse, 'block the user'), this._alert.getErrorDetails(err));
    } finally {
      this.submitting = false;
      this.blockForm.enable();
    }
  }

  /** Returns the key to block, or null if the admin cancelled or no unique match was found. */
  private async resolveRequestorKey(identifier: string): Promise<string | null> {
    let matches: DirectoryUser[];
    try {
      matches = await firstValueFrom(this._user.findDirectoryUsers(identifier));
    } catch (err) {
      await this._alert.error('Unable to look up the user in the directory.', this._alert.getErrorDetails(err));
      return null;
    }

    if (matches.length === 0) {
      await this._alert.warning('No user was found with that GMID or email.', identifier);
      return null;
    }
    if (matches.length > 1) {
      await this._alert.warning(
        'More than one user matched. Enter their email instead.',
        matches.map(m => this.describeDirectoryUser(m)).join('\n\n')
      );
      return null;
    }

    const match = matches[0];
    const person = await firstValueFrom(this.toProfile(match));
    this.profiles.set(match.id.toLowerCase(), person);
    const confirmed = await this._alert.confirm(
      'Block this user from submitting requests?',
      'Confirm Block',
      'Block',
      'Cancel',
      undefined,
      person
    );
    return confirmed ? match.id.toLowerCase() : null;
  }

  private describeDirectoryUser(user: DirectoryUser): string {
    return [
      `Name: ${user.displayName || '—'}`,
      `GMID: ${this.gmidOf(user) || '—'}`,
      `Email: ${user.mail || user.userPrincipalName || '—'}`
    ].join('\n');
  }

  private gmidOf(user: DirectoryUser): string | null {
    if (user.onPremisesSamAccountName) return user.onPremisesSamAccountName.toLowerCase();
    // Same derivation the app uses for the signed-in user's GMID.
    const upnLocal = user.userPrincipalName?.split('@')[0];
    return upnLocal ? upnLocal.substring(0, 6).toLowerCase() : null;
  }

  private toProfile(user: DirectoryUser): Observable<AlertPerson> {
    const photo$: Observable<{ picture: string | null }> = this._user.getUserByGmid(user.id);
    return photo$.pipe(
      map(photo => photo?.picture ?? null),
      catchError(() => of(null)),
      map(photo => ({
        name: user.displayName,
        gmid: this.gmidOf(user),
        email: user.mail || user.userPrincipalName,
        photo
      }))
    );
  }

  private lookupDirectoryUser(identity: string): Observable<DirectoryUser | null> {
    const lookup$ = OBJECT_ID_PATTERN.test(identity)
      ? this._user.getDirectoryUserById(identity)
      : this._user.findDirectoryUsers(identity).pipe(map(users => users.length === 1 ? users[0] : null));
    return lookup$.pipe(catchError(() => of(null)));
  }

  private resolveProfiles(): void {
    // Map each unique identity to the value used for the directory lookup.
    const pending = new Map<string, string>();
    const queue = (identity: string, lookupValue: string) => {
      const key = identity.toLowerCase();
      if (!this.profiles.has(key) && !pending.has(key)) pending.set(key, lookupValue);
    };
    for (const block of this.blocks) {
      queue(block.requestor_key, OBJECT_ID_PATTERN.test(block.requestor_key)
        ? block.requestor_key
        : block.email || block.gmid || block.requestor_key);
      if (block.blocked_by !== RATE_LIMITER) queue(block.blocked_by, block.blocked_by);
    }
    if (!pending.size) return;

    from([...pending]).pipe(
      mergeMap(([key, lookupValue]) => this.lookupDirectoryUser(lookupValue).pipe(
        switchMap(user => user ? this.toProfile(user) : of(null)),
        map(profile => [key, profile] as const)
      ), PROFILE_LOOKUP_CONCURRENCY),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([key, profile]) => this.profiles.set(key, profile));
  }

  /** undefined while loading, null when not found. */
  profileFor(identity: string): AlertPerson | null | undefined {
    return this.profiles.get(identity.toLowerCase());
  }

  async confirmUnblock(block: ActiveUserBlock): Promise<void> {
    if (this.unblockingKey) return;

    const confirmed = await this._alert.confirm(
      'Unblock this user? Their attempt counter will also be reset.',
      'Confirm Unblock',
      'Unblock',
      'Cancel',
      undefined,
      this.requestorPerson(block)
    );
    if (!confirmed || this.unblockingKey) return;

    this.unblockingKey = block.requestor_key;
    this._blocks.unblockUser(block.requestor_key)
      .pipe(
        finalize(() => this.unblockingKey = null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.refresh(),
        error: async (err: HttpErrorResponse) => {
          await this._alert.error(this.describeError(err, 'unblock the user'), this._alert.getErrorDetails(err));
          if (err.status === 404) {
            this.refresh();
          }
        }
      });
  }

  requestorPerson(block: ActiveUserBlock): AlertPerson {
    const profile = this.profileFor(block.requestor_key);
    const gmid = block.gmid || profile?.gmid || null;
    const email = block.email || profile?.email || null;
    const fallbackName = !gmid && !email && !OBJECT_ID_PATTERN.test(block.requestor_key)
      ? block.requestor_key
      : null;
    return {
      name: profile?.name || fallbackName || (profile === undefined ? 'Loading...' : 'Unknown user'),
      gmid,
      email,
      photo: profile?.photo ?? null
    };
  }

  isRateLimiter(blockedBy: string): boolean {
    return blockedBy === RATE_LIMITER;
  }

  blockedByPerson(blockedBy: string): AlertPerson {
    const profile = this.profileFor(blockedBy);
    if (profile) return profile;
    return {
      name: profile === undefined ? 'Loading...' : 'Unknown admin',
      gmid: OBJECT_ID_PATTERN.test(blockedBy) ? null : blockedBy,
      email: null,
      photo: null
    };
  }

  private describeError(err: HttpErrorResponse, action: string): string {
    const serverMessage = err?.error?.message || err?.error?.error;
    switch (err?.status) {
      case 0:
        return `Unable to reach the server to ${action}. Check your connection and try again.`;
      case 400:
        return `Invalid input: ${serverMessage || 'the request was rejected.'}`;
      case 401:
        return 'Your session could not be authenticated. Sign in again and retry.';
      case 403:
        return `You do not have admin access to ${action}.`;
      case 404:
        return 'That user is not currently blocked.';
      case 500:
        return `The server failed to ${action}. Please try again later.`;
      default:
        return serverMessage || `Failed to ${action}.`;
    }
  }
}
